import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import leaveService, { type LeaveRequest } from "../services/leave.service";
import { dateStringBeToCe, dateStringCeToBe } from "../utils/dateUtils";
import { MainLayout } from "../components/layout";
import { useNavigate } from "../lib/router";

export const LeaveListPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Read query parameters from URL
  const searchParams = new URLSearchParams(window.location.search);
  const initialViewMode =
    (searchParams.get("viewMode") as "own" | "all") || "own";
  const initialUserId = searchParams.get("userId") || "";
  const initialStatus = searchParams.get("status") || "all";

  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [viewMode, setViewMode] = useState<"own" | "all">(initialViewMode);
  const [selectedUserId, setSelectedUserId] = useState<string>(initialUserId);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Check if user is admin or HR
  const isAdminOrHR =
    currentUser?.role === "admin" || currentUser?.role === "hr";

  // Update filters when URL changes (for SPA navigation)
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const newViewMode = params.get("viewMode") as "own" | "all";
      const newUserId = params.get("userId");
      const newStatus = params.get("status");

      if (newViewMode && isAdminOrHR) {
        setViewMode(newViewMode);
      }
      if (newUserId !== null) {
        setSelectedUserId(newUserId);
      }
      if (newStatus) {
        setStatusFilter(newStatus);
      }
      // Reset to page 1 when filters change from URL
      setCurrentPage(1);
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", handleUrlChange);
    return () => window.removeEventListener("popstate", handleUrlChange);
  }, [isAdminOrHR]);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState<LeaveRequest | null>(
    null
  );
  const [editForm, setEditForm] = useState({
    startDate: "",
    endDate: "",
    isHalfDayStart: false,
    isHalfDayEnd: false,
    reason: "",
    contactDuringLeave: "",
    emergencyContact: "",
  });

  // Display state for Buddhist Era dates in edit form
  const [editDisplayDates, setEditDisplayDates] = useState({
    startDate: "",
    endDate: "",
  });

  // Fetch users for admin/HR filter dropdown
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users"],
    queryFn: () => leaveService.getUsers(),
    enabled: isAdminOrHR,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch leave requests
  const {
    data: leaveData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["leaveRequests", statusFilter, viewMode, selectedUserId],
    queryFn: () => {
      const params: any = {};

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      if (isAdminOrHR && viewMode === "all") {
        params.allUsers = "true";
        if (selectedUserId) {
          params.userId = selectedUserId;
        }
      }

      return leaveService.getLeaveRequests(params);
    },
    staleTime: 30 * 1000, // 30 seconds
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: (data: { id: string; updates: typeof editForm }) =>
      leaveService.updateLeaveRequest(data.id, data.updates),
    onSuccess: (response) => {
      // Update the request in cache immediately
      if (leaveData?.requests && response.request) {
        const updatedRequests = leaveData.requests.map((req) =>
          req.id === response.request.id ? response.request : req
        );
        queryClient.setQueryData(
          ["leaveRequests", statusFilter, viewMode, selectedUserId],
          { ...leaveData, requests: updatedRequests }
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["leaveRequest", response.request.id],
      });
      setShowEditModal(false);
      setEditingRequest(null);
      alert("แก้ไขคำขอลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: (requestId: string) =>
      leaveService.cancelLeaveRequest(requestId),
    onSuccess: (response) => {
      // Update the request in cache immediately
      if (leaveData?.requests && response.request) {
        const updatedRequests = leaveData.requests.map((req) =>
          req.id === response.request.id ? response.request : req
        );
        queryClient.setQueryData(
          ["leaveRequests", statusFilter, viewMode, selectedUserId],
          { ...leaveData, requests: updatedRequests }
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["leaveRequest", response.request.id],
      });
      alert("ยกเลิกคำขอลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  const handleCancelRequest = (requestId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ที่จะยกเลิกคำขอลานี้?")) {
      return;
    }
    cancelMutation.mutate(requestId);
  };

  const handleOpenEditModal = (request: LeaveRequest) => {
    setEditingRequest(request);
    const startCE = request.startDate.split("T")[0];
    const endCE = request.endDate.split("T")[0];

    setEditForm({
      startDate: startCE,
      endDate: endCE,
      isHalfDayStart: request.isHalfDayStart || false,
      isHalfDayEnd: request.isHalfDayEnd || false,
      reason: request.reason,
      contactDuringLeave: request.contactDuringLeave || "",
      emergencyContact: request.emergencyContact || "",
    });

    // Convert CE dates to BE for display
    setEditDisplayDates({
      startDate: dateStringCeToBe(startCE),
      endDate: dateStringCeToBe(endCE),
    });

    setShowEditModal(true);
  };

  const handleEditSubmit = () => {
    if (!editingRequest) return;

    // Validation
    if (!editForm.startDate || !editForm.endDate) {
      alert("กรุณาระบุวันที่เริ่มต้นและสิ้นสุด");
      return;
    }
    if (!editForm.reason.trim()) {
      alert("กรุณาระบุเหตุผลการลา");
      return;
    }
    if (new Date(editForm.startDate) > new Date(editForm.endDate)) {
      alert("วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด");
      return;
    }

    editMutation.mutate({ id: editingRequest.id, updates: editForm });
  };

  const canEditRequest = (request: LeaveRequest) => {
    // Owner can edit their own pending requests (that haven't started yet)
    // Admin/HR can edit pending requests (including backdated ones)
    const isOwner = request.userId === currentUser?.id;
    const canManage = isAdminOrHR;

    // Admin/HR can edit any pending request (even backdated)
    if (canManage && request.status === "pending") {
      return true;
    }

    // Regular users can only edit their own pending requests that haven't started yet
    return (
      request.status === "pending" &&
      new Date(request.startDate) > new Date() &&
      isOwner
    );
  };

  const canCancelRequest = (request: LeaveRequest) => {
    // Owner can cancel their own requests, or admin/HR/manager can cancel any
    const isOwner = request.userId === currentUser?.id;
    const canManageAll = isAdminOrHR || currentUser?.role === "manager";

    // Admin/HR/Manager can cancel any pending or approved request (even backdated)
    if (
      canManageAll &&
      (request.status === "pending" || request.status === "approved")
    ) {
      return true;
    }

    // Regular users can only cancel their own requests that haven't started yet
    return (
      (request.status === "pending" || request.status === "approved") &&
      new Date(request.startDate) > new Date() &&
      isOwner
    );
  };

  const isBackdatedRequest = (request: LeaveRequest) => {
    return (
      new Date(request.startDate) < new Date(new Date().setHours(0, 0, 0, 0))
    );
  };

  return (
    <MainLayout
      title={
        viewMode === "all" && isAdminOrHR ? "คำขอลาทั้งหมด" : "คำขอลาของฉัน"
      }
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {viewMode === "all" && isAdminOrHR
                  ? "คำขอลาทั้งหมด"
                  : "คำขอลาของฉัน"}
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {viewMode === "all" && isAdminOrHR
                  ? "ดูและจัดการคำขอลาของพนักงานทุกคน"
                  : "ดูและจัดการคำขอลาของคุณ"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/calendar")}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
              >
                📅 ปฏิทิน
              </button>
              <button
                onClick={() => navigate("/leave/request")}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                + สร้างคำขอใหม่
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-4 mb-6">
          <div className="space-y-4">
            {/* Admin/HR View Mode Toggle */}
            {isAdminOrHR && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  โหมดการแสดง:
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setViewMode("own");
                      setSelectedUserId("");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      viewMode === "own"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    คำขอของฉัน
                  </button>
                  <button
                    onClick={() => {
                      setViewMode("all");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      viewMode === "all"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    คำขอทั้งหมด
                  </button>
                </div>
              </div>
            )}

            {/* User Filter (Admin/HR All mode only) */}
            {isAdminOrHR && viewMode === "all" && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  กรองตามพนักงาน:
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => {
                    setSelectedUserId(e.target.value);
                    setCurrentPage(1);
                  }}
                  disabled={isLoadingUsers}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">พนักงานทั้งหมด</option>
                  {usersData?.users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Status Filter */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                กรองตามสถานะ:
              </label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: "all", label: "ทั้งหมด" },
                  { value: "pending", label: "รอพิจารณา" },
                  { value: "approved", label: "อนุมัติ" },
                  { value: "rejected", label: "ปฏิเสธ" },
                  { value: "cancelled", label: "ยกเลิก" },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => {
                      setStatusFilter(value);
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      statusFilter === value
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => refetch()}
                className="ml-auto text-blue-600 hover:text-blue-800 flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                รีเฟรช
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">กำลังโหลดข้อมูลคำขอลา...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p className="font-medium">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
            <p className="text-sm">
              {error instanceof Error
                ? error.message
                : "ข้อผิดพลาดที่ไม่ทราบสาเหตุ"}
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && leaveData?.requests.length === 0 && (
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              ไม่พบคำขอลา
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {statusFilter !== "all"
                ? `คุณไม่มีคำขอลาที่อยู่ในสถานะ "${
                    statusFilter === "pending"
                      ? "รอพิจารณา"
                      : statusFilter === "approved"
                        ? "อนุมัติ"
                        : statusFilter === "rejected"
                          ? "ปฏิเสธ"
                          : statusFilter === "cancelled"
                            ? "ยกเลิก"
                            : statusFilter
                  }"`
                : "คุณยังไม่มีคำขอลา"}
            </p>
            <button
              onClick={() => navigate("/leave/request")}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              สร้างคำขอลาแรกของคุณ
            </button>
          </div>
        )}

        {/* Leave Requests List */}
        {!isLoading &&
          !error &&
          leaveData &&
          leaveData.requests.length > 0 &&
          (() => {
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedRequests = leaveData.requests.slice(
              startIndex,
              endIndex
            );
            const totalPages = Math.ceil(
              leaveData.requests.length / itemsPerPage
            );

            return (
              <>
                <div className="space-y-4">
                  {paginatedRequests.map((request: LeaveRequest) => (
                    <div
                      key={request.id}
                      className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/leaves/${request.id}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {/* Header */}
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">
                              {request.leaveType?.icon || "📅"}
                            </span>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {request.leaveType?.name || "คำขอลา"}
                              </h3>
                              <div className="flex flex-wrap gap-2 text-sm text-gray-500 dark:text-gray-400">
                                {viewMode === "all" && request.user && (
                                  <span className="inline-flex items-center gap-1">
                                    👤 {request.user.firstName}{" "}
                                    {request.user.lastName}
                                    <span className="text-xs text-gray-400">
                                      ({request.user.email})
                                    </span>
                                  </span>
                                )}
                                <span>
                                  📅 ยื่นคำขอเมื่อ{" "}
                                  {formatDate(request.submittedAt)}
                                </span>
                              </div>
                              {/* Indicators */}
                              <div className="flex flex-wrap gap-2 mt-1">
                                {request.user?.id !== currentUser?.id &&
                                  viewMode !== "all" && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                      🤝 สร้างแทน
                                    </span>
                                  )}
                                {isBackdatedRequest(request) && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                    ⏮️ ลาย้อนหลัง
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Backdated Leave Notice */}
                          {isBackdatedRequest(request) && (
                            <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <p className="text-sm text-orange-800 flex items-center gap-2">
                                <span className="text-lg">⏮️</span>
                                <span>
                                  <strong>การลาย้อนหลัง:</strong>{" "}
                                  รายการนี้เป็นการลาที่วันเริ่มต้นอยู่ในอดีต
                                  {isAdminOrHR &&
                                    " (Admin/HR สามารถแก้ไขและยกเลิกได้)"}
                                </span>
                              </p>
                            </div>
                          )}

                          {/* Dates */}
                          <div className="flex items-center gap-6 mb-3">
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="font-medium">
                                {formatDate(request.startDate)} →{" "}
                                {formatDate(request.endDate)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {request.totalDays ||
                                calculateDuration(
                                  request.startDate,
                                  request.endDate
                                )}{" "}
                              วัน
                              {request.isHalfDayStart && " (ครึ่งวันแรก)"}
                              {request.isHalfDayEnd && " (ครึ่งวันสุดท้าย)"}
                            </div>
                          </div>

                          {/* Reason */}
                          <p className="text-gray-700 dark:text-gray-300 mb-3 line-clamp-2">
                            {request.reason}
                          </p>

                          {/* Metadata */}
                          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                            {request.contactDuringLeave && (
                              <div className="flex items-center gap-1">
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                  />
                                </svg>
                                มีช่องทางติดต่อ
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Status Badge and Actions */}
                        <div className="flex flex-col items-end gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(
                              request.status
                            )}`}
                          >
                            {request.status === "pending"
                              ? "รอพิจารณา"
                              : request.status === "approved"
                                ? "อนุมัติ"
                                : request.status === "rejected"
                                  ? "ปฏิเสธ"
                                  : request.status === "cancelled"
                                    ? "ยกเลิก"
                                    : request.status.toUpperCase()}
                          </span>
                          {request.approvedAt && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              อนุมัติเมื่อ {formatDate(request.approvedAt)}
                            </p>
                          )}
                          {request.rejectedAt && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              ปฏิเสธเมื่อ {formatDate(request.rejectedAt)}
                            </p>
                          )}
                          {request.cancelledAt && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              ยกเลิกเมื่อ {formatDate(request.cancelledAt)}
                            </p>
                          )}

                          {/* Action Buttons */}
                          {(canEditRequest(request) ||
                            canCancelRequest(request)) && (
                            <div
                              className="flex gap-2 mt-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canEditRequest(request) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEditModal(request);
                                  }}
                                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                >
                                  แก้ไข
                                </button>
                              )}
                              {canCancelRequest(request) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelRequest(request.id);
                                  }}
                                  disabled={cancelMutation.isPending}
                                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                                >
                                  {cancelMutation.isPending
                                    ? "กำลังยกเลิก..."
                                    : "ยกเลิก"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center items-center gap-2">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← ก่อนหน้า
                    </button>

                    <div className="flex gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => {
                          // แสดงหน้าแรก, หน้าสุดท้าย, หน้าปัจจุบัน และหน้าใกล้เคียง
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-4 py-2 rounded-lg ${
                                  currentPage === page
                                    ? "bg-blue-600 text-white"
                                    : "bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                                }`}
                              >
                                {page}
                              </button>
                            );
                          } else if (
                            page === currentPage - 2 ||
                            page === currentPage + 2
                          ) {
                            return (
                              <span key={page} className="px-2 py-2">
                                ...
                              </span>
                            );
                          }
                          return null;
                        }
                      )}
                    </div>

                    <button
                      onClick={() =>
                        setCurrentPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ถัดไป →
                    </button>
                  </div>
                )}

                {/* Pagination Info */}
                <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
                  แสดง {startIndex + 1}-
                  {Math.min(endIndex, leaveData.requests.length)} จาก{" "}
                  {leaveData.requests.length} รายการ
                  {leaveData.total > leaveData.requests.length &&
                    ` (ทั้งหมด ${leaveData.total} รายการ)`}
                </div>
              </>
            );
          })()}

        {/* Edit Modal */}
        {showEditModal && editingRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full my-8">
              <h3 className="text-xl font-semibold mb-4 text-blue-600 dark:text-blue-400">
                ✏️ แก้ไขคำขอลา
              </h3>

              <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Leave Type Info */}
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">ประเภทการลา</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {editingRequest.leaveType?.icon}{" "}
                    {editingRequest.leaveType?.name}
                  </p>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      วันที่เริ่มต้น <span className="text-red-500">*</span>
                      {isAdminOrHR && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                          (สามารถเลือกวันที่ย้อนหลังได้)
                        </span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={editDisplayDates.startDate}
                      onChange={(e) => {
                        const ceDate = dateStringBeToCe(e.target.value);
                        setEditForm({ ...editForm, startDate: ceDate });
                        setEditDisplayDates({
                          ...editDisplayDates,
                          startDate: e.target.value,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      required
                    />
                    {editDisplayDates.startDate && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        พ.ศ. {editDisplayDates.startDate.split("-")[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      วันที่สิ้นสุด <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={editDisplayDates.endDate}
                      onChange={(e) => {
                        const ceDate = dateStringBeToCe(e.target.value);
                        setEditForm({ ...editForm, endDate: ceDate });
                        setEditDisplayDates({
                          ...editDisplayDates,
                          endDate: e.target.value,
                        });
                      }}
                      min={editDisplayDates.startDate}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      required
                    />
                    {editDisplayDates.endDate && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        พ.ศ. {editDisplayDates.endDate.split("-")[0]}
                      </p>
                    )}
                  </div>
                </div>

                {/* Backdated Leave Warning */}
                {isAdminOrHR &&
                  editForm.startDate &&
                  new Date(editForm.startDate) <
                    new Date(new Date().setHours(0, 0, 0, 0)) && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 rounded-lg">
                      <p className="text-sm flex items-center">
                        <span className="text-lg mr-2">⚠️</span>
                        <span>
                          <strong>คำเตือน:</strong>{" "}
                          คุณกำลังแก้ไขคำขอลาให้เป็นวันที่ย้อนหลัง
                        </span>
                      </p>
                    </div>
                  )}

                {/* Half Day Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editHalfDayStart"
                      checked={editForm.isHalfDayStart}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          isHalfDayStart: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="editHalfDayStart"
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      ลาครึ่งวันแรก
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editHalfDayEnd"
                      checked={editForm.isHalfDayEnd}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          isHalfDayEnd: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="editHalfDayEnd"
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      ลาครึ่งวันสุดท้าย
                    </label>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    เหตุผลการลา <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editForm.reason}
                    onChange={(e) =>
                      setEditForm({ ...editForm, reason: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={3}
                    placeholder="ระบุเหตุผลการลา..."
                    required
                  />
                </div>

                {/* Contact During Leave */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ช่องทางติดต่อระหว่างลา
                  </label>
                  <input
                    type="text"
                    value={editForm.contactDuringLeave}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        contactDuringLeave: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="เบอร์โทร, อีเมล, หรือช่องทางอื่นๆ"
                  />
                </div>

                {/* Emergency Contact */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ผู้ติดต่อฉุกเฉิน
                  </label>
                  <input
                    type="text"
                    value={editForm.emergencyContact}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        emergencyContact: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="ชื่อและเบอร์ติดต่อผู้ที่สามารถติดต่อได้"
                  />
                </div>

                {/* Warning for Admin/HR */}
                {isAdminOrHR && editingRequest.userId !== currentUser?.id && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      ⚠️ คุณกำลังแก้ไขคำขอลาของพนักงานคนอื่น ในฐานะ{" "}
                      {currentUser?.role === "admin" ? "Admin" : "HR"}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingRequest(null);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  disabled={editMutation.isPending}
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={editMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {editMutation.isPending ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LeaveListPage;
